
import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { storiesData } from "@/data/stories";
import { sdgsData } from "@/data/sdgs";
import { ChevronRight } from "lucide-react";
import { getImagePreviewPath } from "@/components/admin/stories/utils/imageHandlers";

export const FeaturedStories: React.FC = () => {
  const { t, language } = useLanguage();
  const featuredStories = storiesData.filter(story => story.featured);

  // Debug stories on initial render with improved logging
  useEffect(() => {
    console.log("FeaturedStories loaded with stories:", 
      featuredStories.map(story => ({
        id: story.id,
        title: story.title.en,
        imagePath: story.image,
        resolvedPath: getImagePreviewPath(story.image)
      }))
    );
  }, []);

  // Enhanced image error handling
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    console.log("Image failed to load:", e.currentTarget.src);
    
    // Try to get a working fallback based on story ID if possible
    const storyId = e.currentTarget.getAttribute('data-story-id');
    if (storyId) {
      const numericId = parseInt(storyId);
      if (!isNaN(numericId)) {
        let fallbackPath;
        switch (numericId) {
          case 1: 
            fallbackPath = "/lovable-uploads/d2cfe4d7-2bad-4598-aced-11e4ac3e36d2.png"; // Solar panel image
            break;
          case 2:
            fallbackPath = "/lovable-uploads/5345efbc-ab4d-4769-a9ac-d2d94af7d692.png"; // Farming image
            break;
          case 3:
            fallbackPath = "/lovable-uploads/7e3bb041-335c-45a2-a9ec-92bbc276ca44.png"; // Updated startup image
            break;
          case 4:
            fallbackPath = "/lovable-uploads/d6a911f7-e55a-4331-a528-f6d5dcc6ae41.png"; // Water image
            break;
          case 5:
            fallbackPath = "/lovable-uploads/1b9e0703-9313-4b25-a35e-76332d449bbe.png"; // Women image
            break;
          default:
            fallbackPath = "/lovable-uploads/0bed4794-3e56-4a50-9f99-773ee667b61c.png"; // Default image
        }
        console.log(`Using fallback image for story ID ${numericId}:`, fallbackPath);
        e.currentTarget.src = fallbackPath;
        return;
      }
    }
    
    // Default fallback
    e.currentTarget.src = "/placeholder.svg";
    e.currentTarget.classList.add("bg-gray-100");
  };

  return (
    <section className="py-16 md:py-24 bg-palsustain-cream">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-palsustain-green mb-3">
            {t("home.stories.title")}
          </h2>
          <p className="text-lg text-palsustain-charcoal/70 max-w-3xl mx-auto">
            {t("home.stories.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {featuredStories.map((story) => {
            const relatedSDGs = sdgsData.filter(sdg => story.sdgIds.includes(sdg.id));
            
            // Get appropriate image for story using our utility function
            const imagePath = getImagePreviewPath(story.image);
            console.log(`Story ${story.id} image path:`, {
              original: story.image,
              resolved: imagePath
            });
            
            return (
              <article key={story.id} className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-lg transform hover:-translate-y-1">
                <div className="relative h-48 overflow-hidden bg-gray-100">
                  <img 
                    src={imagePath}
                    alt={language === "en" ? story.title.en : story.title.ar} 
                    className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                    onError={handleImageError}
                    loading="lazy"
                    data-story-id={story.id}
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    {relatedSDGs.slice(0, 3).map((sdg) => (
                      <Link 
                        key={sdg.id}
                        to={`/sdgs/${sdg.slug}`}
                        className="inline-flex items-center justify-center w-7 h-7 rounded-full shadow-md" 
                        style={{ backgroundColor: sdg.color }} 
                        aria-label={language === "en" ? sdg.title.en : sdg.title.ar}
                      >
                        <span className="text-xs font-bold text-white">{sdg.id}</span>
                      </Link>
                    ))}
                  </div>
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-xl font-bold mb-3">
                    {language === "en" ? story.title.en : story.title.ar}
                  </h3>
                  <p className="text-palsustain-charcoal/80 mb-4 flex-grow">
                    {language === "en" ? story.summary.en : story.summary.ar}
                  </p>
                  <Link 
                    to={`/stories/${story.slug}`} 
                    className="inline-flex items-center text-palsustain-green hover:text-palsustain-green/80 transition-colors font-medium"
                  >
                    {t("home.stories.readmore")}
                    <ChevronRight className={`ml-1 h-4 w-4 ${language === "ar" ? "transform rotate-180" : ""}`} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedStories;
